import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import ScoreCard from 'components/field/ScoreCard';
import ConfirmationDialog from 'components/global/ConfirmationDialog';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Tag from 'components/global/Tag';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import TableFilterTypes from 'components/table/Filters/TableFilterTypes';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useMediaQuery from 'hooks/useMediaQuery';

import { getOTOPackageList } from 'pages/OTOModules/OTOPackageListSlice';
import ShippingDetailsModal from 'pages/PriceCalculator/ShipingDetailsModal/ShippingDetailsModal';
import SubscriptionAccountModal from 'pages/Settings/Payment/SubscriptionAccount/SubscriptionAccountModal';
import EditOrderForm from 'pages/Shipments/Shipments/OrderForm/EditOrderForm';

import { Breakpoints } from 'utils/const.styles';
import PATHS from 'utils/paths';

import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import {
	faBuilding,
	faCabinetFiling,
	faCircleInfo,
	faPersonCarryBox,
	faTruckFlatbed
} from '@fortawesome/pro-regular-svg-icons';

import * as S from './ShippingOptions.styled';
import VerifyCR from './VerifyCR';

const getPackagePrice = (item, selectedPackage) => {
	switch (selectedPackage) {
		case 'FreePackage':
			return item.price;
		case 'StarterPackage':
			return item.starterPrice ?? item.price;
		case 'ScalePackage':
			return item.scalePrice ?? item.price;
		default:
			return item.price;
	}
};

const createNumericSorter = (accessor) => (rowA, rowB) => {
	const valueA = accessor(rowA.original) ?? 0;
	const valueB = accessor(rowB.original) ?? 0;

	if (valueA > valueB) {
		return -1;
	}
	if (valueA < valueB) {
		return 1;
	}
	return 0;
};

const ShippingOptions = ({
	dcCompanyData,
	nextStep,
	setTableRef,
	isIdVerificationRequired,
	setHasOptionalPickup,
	setNoticeData,
	selectedPackage,
	setIsAggregatorBulletDelivery,
	setIsLockerDelivery,
	isAggregatorBulletDelivery,
	clientCRDocumentStatus,
	isQuickShipment,
	resetStep,
	setDcCompanyData,
	addPaymentMethodRequired,
	setIsPudoDelivery
}) => {
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const formMethods = useFormContext();
	const { setValue, control, watch } = formMethods;
	const orderID = useWatch({
		control,
		name: 'orderID'
	});
	const isInternationalOrder = watch('originAddress.countryName') !== watch('destinationAddress.countryName');

	const { CRDocStatus, FLDocStatus } = useSelector((state) => state.user.generalSettings);
	const onboarding = useSelector((state) => state.instruction);

	const isVerified = useMemo(
		() => CRDocStatus === 'VERIFIED' || FLDocStatus === 'VERIFIED',
		[CRDocStatus, FLDocStatus]
	);

	const isAuthorized = useAccessControl();

	const columns = useMemo(
		() => [
			{
				id: 'courier',
				header: () => <InformationHeader label="courier" description="courierDescription" />,
				accessorKey: 'nameAr',
				size: isMobile ? 120 : 200,
				renderCell: ({ row }) => (
					<S.CourierNameContainer>
						<Image url={row.original.logo} width="4rem" height="4rem" />
						<Typography fontWeight="600">{row.original.nameAr}</Typography>
					</S.CourierNameContainer>
				),
				filterFn: TableFilterTypes.multiSelection,
				showOnMobile: true,
				pin: 'left',
				showInColumnsMenu: false,
				hasRowClick: true
			},
			...(isAuthorized('/price-calculator-dc-rating')
				? [
						{
							id: 'overallRating',
							header: () => (
								<InformationHeader label="overallRating" description="overallRatingDescription" />
							),
							accessorKey: 'score5',
							size: isMobile ? 100 : 180,
							renderCell: ({ row }) => <CourierScoreContainer data={row.original} />,
							filterFn: TableFilterTypes.rating,
							enableHiding: false,
							sortingFn: createNumericSorter((item) => item.score5)
						}
					]
				: []),
			{
				id: 'serviceType',
				header: () => <InformationHeader label="serviceType" description="serviceTypeDescription" />,
				accessorKey: 'serviceType',
				size: 150,
				renderCell: ({ row }) => <Typography>{row.original.serviceType}</Typography>,
				filterFn: TableFilterTypes.multiSelection,
				enableHiding: false
			},
			{
				id: 'deliveryTime',
				header: () => <InformationHeader label="deliveryTime" description="avgDeliveryDescription" />,
				accessorKey: 'deliveryTime',
				size: 150,
				renderCell: ({ row }) => <Tag variant="active" label={row.original.deliveryTime} />,
				filterFn: TableFilterTypes.multiSelection,
				enableHiding: false
			},
			{
				id: 'pickupDropoff',
				header: () => <InformationHeader label="pickupDropoff" description="pickupDropoffDescription" />,
				accessorKey: 'pickupType',
				size: 150,
				renderCell: PickupDropoffCell,
				filterFn: TableFilterTypes.multiSelection,
				enableHiding: false
			},
			{
				id: 'deliveryType',
				header: () => <InformationHeader label="deliveryType" description="deliveryTypeDescription" />,
				accessorKey: 'deliveryType',
				size: 150,
				renderCell: DeliveryTypeCell,
				filterFn: TableFilterTypes.multiSelection,
				enableHiding: false
			},
			...(isAuthorized('/price-calculator-dc-rating')
				? [
						{
							id: 'pickupSuccessRate',
							header: () => (
								<InformationHeader
									label="pickupSuccessRate"
									description="pickupSuccessRateDescription"
								/>
							),
							accessorKey: 'pickedUpPercentage',
							size: isMobile ? 100 : 180,
							renderCell: ({ row }) => {
								const value = row.original.pickedUpPercentage;
								return !isNaN(value) && value !== 0 ? (
									<ScoreCard value={value} disabled size="small" />
								) : (
									<Typography fontWeight="500">priceCalculator.scoreNA</Typography>
								);
							},
							filterFn: TableFilterTypes.rating,
							sortingFn: createNumericSorter((item) => item.pickedUpPercentage)
						},
						{
							id: 'deliverySuccessRate',
							header: () => (
								<InformationHeader
									label="deliverySuccessRate"
									description="deliverySuccessRateDescription"
								/>
							),
							accessorKey: 'deliveredPercentage',
							size: isMobile ? 100 : 180,
							renderCell: ({ row }) => {
								const value = row.original.deliveredPercentage;
								return !isNaN(value) && value !== 0 ? (
									<ScoreCard value={value} disabled size="small" />
								) : (
									<Typography fontWeight="500">priceCalculator.scoreNA</Typography>
								);
							},
							filterFn: TableFilterTypes.rating,
							sortingFn: createNumericSorter((item) => item.deliveredPercentage)
						},
						{
							id: 'pickupSLACompliance',
							header: () => (
								<InformationHeader
									label="pickupSLACompliance"
									description="pickupSLAComplianceDescription"
								/>
							),
							accessorKey: 'pickupSlaSuccessPercentage',
							size: isMobile ? 100 : 180,
							renderCell: ({ row }) => {
								const value = row.original.pickupSlaSuccessPercentage;
								return !isNaN(value) && value !== 0 ? (
									<ScoreCard value={value} disabled size="small" />
								) : (
									<Typography fontWeight="500">priceCalculator.scoreNA</Typography>
								);
							},
							filterFn: TableFilterTypes.rating,
							sortingFn: createNumericSorter((item) => item.pickupSlaSuccessPercentage)
						},
						{
							id: 'deliverySLACompliance',
							header: () => (
								<InformationHeader
									label="deliverySLACompliance"
									description="deliverySLAComplianceDescription"
								/>
							),
							accessorKey: 'deliverySlaSuccessPercentage',
							size: isMobile ? 100 : 180,
							renderCell: ({ row }) => {
								const value = row.original.deliverySlaSuccessPercentage;
								return !isNaN(value) && value !== 0 ? (
									<ScoreCard value={value} disabled size="small" />
								) : (
									<Typography fontWeight="500">priceCalculator.scoreNA</Typography>
								);
							},
							filterFn: TableFilterTypes.rating,
							sortingFn: createNumericSorter((item) => item.deliverySlaSuccessPercentage)
						},

						{
							id: 'lossAndDamage',
							header: () => (
								<InformationHeader label="lossAndDamage" description="lossAndDamageDescription" />
							),
							accessorKey: 'notLostPercentage',
							size: isMobile ? 100 : 180,
							renderCell: ({ row }) => {
								const value = row.original.notLostPercentage;
								return !isNaN(value) && value !== 0 ? (
									<ScoreCard value={value} disabled size="small" />
								) : (
									<Typography fontWeight="500">priceCalculator.scoreNA</Typography>
								);
							},
							filterFn: TableFilterTypes.rating,
							sortingFn: createNumericSorter((item) => item.notLostPercentage)
						}
					]
				: []),
			{
				id: 'price',
				header: () => <InformationHeader label="price" description="priceDescription" />,
				accessorKey: 'price',
				size: isMobile ? 50 : 100,
				renderCell: ({ row }) => <PriceCell row={row} selectedPackage={selectedPackage} />,
				filterFn: TableFilterTypes.float,
				showOnMobile: true,
				pin: 'right',
				showInColumnsMenu: false,
				sortingFn: createNumericSorter((item) => getPackagePrice(item, selectedPackage))
			},
			{
				id: 'createShipment',
				header: '',
				size: isMobile ? 50 : 100,
				renderCell: ({ row }) =>
					addPaymentMethodRequired ? (
						<Button
							label={
								isInternationalOrder
									? row?.original?.needToVerifyCrDocStatus
										? 'verify'
										: 'select'
									: onboarding.shipmentCheck && row?.original?.needToVerifyCrDocStatus
										? 'verify'
										: 'select'
							}
							fullWidth
							disabled
							disabledTooltip="priceCalculator.missingPaymentMethodDisabledButtonTooltip"
						/>
					) : (
							isInternationalOrder
								? row?.original?.needToVerifyCrDocStatus && !isVerified
								: onboarding.shipmentCheck && row?.original?.needToVerifyCrDocStatus && !isVerified
					  ) ? (
						<VerifyButton clientCRDocumentStatus={clientCRDocumentStatus} />
					) : selectedPackage ? (
						<UpgradePlanButton selectedPackage={selectedPackage} />
					) : (
						<CreateShipmentButton
							nextStep={nextStep}
							isIdVerificationRequired={isIdVerificationRequired}
							setIsAggregatorBulletDelivery={setIsAggregatorBulletDelivery}
							setIsLockerDelivery={setIsLockerDelivery}
							setIsPudoDelivery={setIsPudoDelivery}
							isAggregatorBulletDelivery={isAggregatorBulletDelivery}
							setHasOptionalPickup={setHasOptionalPickup}
							setNoticeData={setNoticeData}
							rowData={row.original}
							isQuickShipment={isQuickShipment}
						/>
					),
				showOnMobile: true,
				pin: 'right',
				showInColumnsMenu: false
			}
		],
		[isMobile, setHasOptionalPickup, selectedPackage, addPaymentMethodRequired, isVerified]
	);

	const handleEditOrderButtonClick = () => {
		if (isQuickShipment) {
			setDcCompanyData(null);
			resetStep();
			return;
		}
		NiceModal.show(EditOrderForm, {
			id: orderID,
			hasCityError: watch('error') === 'city_not_found'
		}).then((response) => {
			if (response.success) {
				setValue('editMode', true);
				setValue('reloadPrices', true);
			}
		});
	};

	return (
		<Table
			key={selectedPackage ? `${selectedPackage}Table` : 'PriceCalculatorTable'}
			ref={setTableRef}
			noRecordsFound={
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<NoRecordsFound
						title={
							watch('error')
								? isQuickShipment
									? `price-calculator.quickShipment.${watch('error')}.title`
									: `price-calculator.${watch('error')}.title`
								: 'shippingOptions.noRecordsFoundTitle'
						}
						subTitle={
							watch('error')
								? isQuickShipment
									? `price-calculator.quickShipment.${watch('error')}.description`
									: `price-calculator.${watch('error')}.description`
								: 'shippingOptions.noRecordsFoundSubTitle'
						}
						actionButton={
							orderID ? (
								<Button
									label={isQuickShipment ? 'quickShipment.cityNotFoundButton' : 'editOrder'}
									onClick={handleEditOrderButtonClick}
								/>
							) : null
						}
					/>
				</div>
			}
			columns={columns}
			data={dcCompanyData}
			storageKey="priceCalculator"
			filterable={!isMobile}
			showColumnsMenu={!isMobile}
			showHeader={!isMobile}
		/>
	);
};

const VerifyButton = ({ clientCRDocumentStatus }) => {
	const [verifyButtonRef, setVerifyButtonRef] = useState(null);
	const navigate = useNavigate();
	const isAuthorized = useAccessControl();
	return isAuthorized('/company-settings/KSAInvoiceType') ? (
		<Button label="verify" fullWidth onClick={() => NiceModal.show(VerifyCR)} />
	) : (
		<>
			<Button label="verify" fullWidth ref={setVerifyButtonRef} />
			<ConfirmationDialog
				buttonRef={verifyButtonRef}
				placement="bottom-end"
				title="priceCalculator.verifyCrDocStatusTitle"
				subTitle={
					clientCRDocumentStatus === 'WAITING_VERIFICATION'
						? 'companySettings.WaitingVerificationFromPriceCalc'
						: 'priceCalculator.verifyCrDocStatusSubtitle'
				}
				rightLabel="priceCalculator.goToSettings"
				rightAction={() => navigate(PATHS.GENERAL_SETTINGS)}
				rightIcon={faCheck}
				leftLabel="cancel"
				leftIcon={faTimes}
			/>
		</>
	);
};

const CourierScoreContainer = ({ data }) => {
	if (isNaN(data.score5) || data.score5 === 0)
		return <Typography fontWeight="500">priceCalculator.scoreNA</Typography>;

	const isNumberPickedUpPercentage = !isNaN(data.pickedUpPercentage);
	const isNumberDeliveredPercentage = !isNaN(data.deliveredPercentage);
	const isNumberPickupSlaSuccessPercentage = !isNaN(data.pickupSlaSuccessPercentage);
	const isNumberDeliverySlaSuccessPercentage = !isNaN(data.deliverySlaSuccessPercentage);
	const isNumberNotLostPercentage = !isNaN(data.notLostPercentage);
	const hasPickedUpPercentage = isNumberPickedUpPercentage && data.pickedUpPercentage !== 0;
	const hasDeliveredPercentage = isNumberDeliveredPercentage && data.deliveredPercentage !== 0;
	const hasPickupSlaSuccessPercentage = isNumberPickupSlaSuccessPercentage && data.pickupSlaSuccessPercentage !== 0;
	const hasDeliverySlaSuccessPercentage =
		isNumberDeliverySlaSuccessPercentage && data.deliverySlaSuccessPercentage !== 0;
	const hasNotLostPercentage = isNumberNotLostPercentage && data.notLostPercentage !== 0;
	const hasAnyValue =
		hasPickedUpPercentage ||
		hasDeliveredPercentage ||
		hasPickupSlaSuccessPercentage ||
		hasDeliverySlaSuccessPercentage ||
		hasNotLostPercentage;

	return (
		<S.CourierScore>
			<Tooltip
				title={
					hasAnyValue ? (
						<S.ScoreCardTooltip>
							{[
								{
									condition: isNumberPickedUpPercentage,
									label: 'pickupSuccessRate',
									value: data.pickedUpPercentage
								},
								{
									condition: isNumberDeliveredPercentage,
									label: 'deliverySuccessRate',
									value: data.deliveredPercentage
								},
								{
									condition: isNumberPickupSlaSuccessPercentage,
									label: 'pickupSLACompliance',
									value: data.pickupSlaSuccessPercentage
								},
								{
									condition: isNumberDeliverySlaSuccessPercentage,
									label: 'deliverySLACompliance',
									value: data.deliverySlaSuccessPercentage
								},
								{
									condition: isNumberNotLostPercentage,
									label: 'lossAndDamage',
									value: data.notLostPercentage
								}
							].map(
								({ condition, label, value }) =>
									condition && (
										<S.ScoreCardTooltipRow key={label}>
											<Typography color="var(--alwaysWhiteColor)" variant="caption" weight="500">
												{label}
											</Typography>
											<ScoreCard value={value} textColor="var(--alwaysWhiteColor)" disabled />
										</S.ScoreCardTooltipRow>
									)
							)}
						</S.ScoreCardTooltip>
					) : undefined
				}
				placement="top"
				containerCss={css`
					max-width: 40rem;
				`}>
				<ScoreCard value={data.score5} disabled size="small" />
			</Tooltip>
		</S.CourierScore>
	);
};

const UpgradePlanButton = ({ selectedPackage }) => {
	const dispatch = useDispatch();

	return (
		<Button
			label="upgrade"
			onClick={async () => {
				const packages = await dispatch(getOTOPackageList()).unwrap();
				NiceModal.show(SubscriptionAccountModal, {
					selectedPackage: packages.find((item) => item.planName === selectedPackage)
				});
			}}
		/>
	);
};

const CreateShipmentButton = ({
	nextStep,
	setHasOptionalPickup,
	rowData,
	setNoticeData,
	setIsAggregatorBulletDelivery,
	setIsLockerDelivery,
	setIsPudoDelivery
}) => {
	const { t } = useTranslation();
	const formMethods = useFormContext();
	const { control, setValue, getValues, setError } = formMethods;
	const isCreateShipmentLoading = useSelector((state) => state.layout.isCreateShipmentLoading);

	const editMode = useWatch({
		control,
		name: 'editMode'
	});

	const checkPickupDropoff = async () => {
		setNoticeData(rowData.notice || null);

		if (rowData.serviceType === 'IMPORT') {
			nextStep();
			return;
		}

		setHasOptionalPickup(rowData.pickupType === 'PICKUP_BY_DC_OR_BRANCH_DROP_OF');
		nextStep();
	};

	const handleSubmit = async () => {
		const result = await NiceModal.show(ShippingDetailsModal, {
			data: rowData
		});
		setIsAggregatorBulletDelivery(rowData.serviceType === 'BULLET');
		setIsLockerDelivery(rowData.serviceType === 'LOCKER_DELIVERY');
		setIsPudoDelivery(rowData.serviceType === 'PUDO');
		if (!result?.proceed) {
			setIsAggregatorBulletDelivery(false);
			setIsLockerDelivery(false);
			setIsPudoDelivery(false);
			return;
		}

		const orderID = getValues('orderID');
		if (!orderID) {
			setValue('editMode', true);
			setError(
				'orderID',
				{
					type: 'required',
					message: t('priceCalculator.orderIDRequiredForCreateShipment')
				},
				{ shouldFocus: true }
			);
			return;
		}

		setValue('priceConfig', rowData);

		checkPickupDropoff();
	};

	return (
		<Button
			label="select"
			disabled={editMode || isCreateShipmentLoading}
			disabledTooltip={editMode && 'priceCalculator.editModeActiveShipmentButtonTooltip'}
			onClick={handleSubmit}
			fullWidth
		/>
	);
};

const InformationHeader = ({ label, description }) => (
	<S.InformationHeaderContainer>
		<Typography color="var(--black)" fontWeight="500">
			{label}
		</Typography>
		{description && (
			<Tooltip title={description}>
				<Icon icon={faCircleInfo} color="var(--content-3)" size="small" />
			</Tooltip>
		)}
	</S.InformationHeaderContainer>
);

const PriceCell = ({ row, selectedPackage }) => {
	const { preDiscountPrice, currency, firstPrice } = row.original;
	const [oldPrice, setOldPrice] = useState(null);

	useEffect(() => {
		if (firstPrice && firstPrice > getPackagePrice(row.original, selectedPackage)) {
			setOldPrice(firstPrice);
		} else if (preDiscountPrice && preDiscountPrice > getPackagePrice(row.original, selectedPackage)) {
			setOldPrice(preDiscountPrice);
		}
	}, [preDiscountPrice, firstPrice]);

	return (
		<S.CellContainer
			style={{
				alignItems: 'center'
			}}>
			{oldPrice && oldPrice !== getPackagePrice(row.original, selectedPackage) && (
				<Typography
					color="var(--red)"
					withCurrency
					currency={currency}
					style={{
						textDecoration: 'line-through'
					}}>
					{oldPrice}
				</Typography>
			)}
			<Typography fontSize="1.8rem" fontWeight="700" withCurrency currency={currency}>
				{getPackagePrice(row.original, selectedPackage)}
			</Typography>
		</S.CellContainer>
	);
};

const PickupDropoffCell = ({ row }) => {
	const { pickupType } = row.original;
	return (
		<S.CellContainer>
			{(pickupType === 'BRANCH_DROP_OFF' || pickupType === 'PICKUP_BY_DC_OR_BRANCH_DROP_OF') && (
				<S.PickupDropoffCell>
					<Icon icon={faPersonCarryBox} />
					<Typography>BRANCH_DROP_OFF</Typography>
				</S.PickupDropoffCell>
			)}
			{(pickupType === 'PICKUP_BY_DC' ||
				pickupType === 'PICKUP_BY_DC_OR_BRANCH_DROP_OF' ||
				pickupType === 'PICKUP_BY_DC_OR_LOCKER_DROP_OF') && (
				<S.PickupDropoffCell>
					<Icon icon={faTruckFlatbed} />
					<Typography>PICKUP_BY_DC</Typography>
				</S.PickupDropoffCell>
			)}

			{(pickupType === 'LOCKER_DROP_OFF' || pickupType === 'PICKUP_BY_DC_OR_LOCKER_DROP_OF') && (
				<S.PickupDropoffCell>
					<Icon icon={faCabinetFiling} />
					<Typography>LOCKER_DROP_OFF</Typography>
				</S.PickupDropoffCell>
			)}

			{pickupType === 'DELIVERED_TO_DC_WAREHOUSE' && (
				<S.PickupDropoffCell>
					<Icon icon={faPersonCarryBox} />
					<Typography>DELIVERED_TO_DC_WAREHOUSE</Typography>
				</S.PickupDropoffCell>
			)}
		</S.CellContainer>
	);
};

const DeliveryTypeCell = ({ row }) => {
	const { deliveryType } = row.original;

	return (
		<S.CellContainer>
			{deliveryType === 'PICKUP_BY_CUSTOMER' ||
				(deliveryType === 'TO_CUSTOMER_DOORSTEP_OR_PICKUP_BY_CUSTOMER' && (
					<S.PickupDropoffCell>
						<Icon icon={faPersonCarryBox} />
						<Typography>PICKUP_BY_CUSTOMER</Typography>
					</S.PickupDropoffCell>
				))}
			{(deliveryType === 'TO_CUSTOMER_DOORSTEP' ||
				deliveryType === 'TO_CUSTOMER_DOORSTEP_OR_PICKUP_BY_CUSTOMER') && (
				<S.PickupDropoffCell>
					<Icon icon={faBuilding} />
					<Typography>TO_CUSTOMER_DOORSTEP</Typography>
				</S.PickupDropoffCell>
			)}

			{deliveryType === 'PICKUP_BY_CUSTOMER' && (
				<S.PickupDropoffCell>
					<Icon icon={faPersonCarryBox} />
					<Typography>PICKUP_BY_CUSTOMER</Typography>
				</S.PickupDropoffCell>
			)}

			{deliveryType === 'LOCKER' && (
				<S.PickupDropoffCell>
					<Icon icon={faCabinetFiling} />
					<Typography>LOCKER</Typography>
				</S.PickupDropoffCell>
			)}
		</S.CellContainer>
	);
};

export default ShippingOptions;
